import type { FortuneCellData, FortuneSheet } from './types';
import type { SheetWorkerResponse } from './sheetWorkerProtocol';

/**
 * A parsed workbook crosses from the worker to the page in parts: each sheet's settings, then its
 * cells in chunks. One message holding every cell was deserialized on the page in a single task
 * (2.4 s for 424k cells at 4x CPU throttle, the tab frozen); each part is its own short task.
 */
export const PARSE_PART_CELLS = 20_000;

type ParsePart = Extract<SheetWorkerResponse, { kind: 'parse-part' }>;
type ParseDone = Extract<SheetWorkerResponse, { kind: 'parse' }>;

/** The messages that carry `sheets` (worker side), the last one the job's completion. */
export const toParseParts = (id: number, sheets: FortuneSheet[], chunk = PARSE_PART_CELLS): (ParsePart | ParseDone)[] => {
    const parts: (ParsePart | ParseDone)[] = [];
    sheets.forEach((sheet, index) => {
        const { celldata = [], ...settings } = sheet;
        parts.push({ id, ok: true, kind: 'parse-part', index, sheet: settings, cells: celldata.slice(0, chunk) });
        for (let from = chunk; from < celldata.length; from += chunk) {
            parts.push({ id, ok: true, kind: 'parse-part', index, cells: celldata.slice(from, from + chunk) });
        }
    });
    parts.push({ id, ok: true, kind: 'parse', sheets: [], parts: sheets.length });
    return parts;
};

/** Collects the parts of one job (page side) and rebuilds its sheets. */
export const createParseAssembler = () => {
    const slots: { sheet?: Omit<FortuneSheet, 'celldata'>; cells: FortuneCellData[][] }[] = [];
    return {
        add: (part: ParsePart): void => {
            const slot = slots[part.index] ?? { cells: [] };
            slots[part.index] = slot;
            if (part.sheet) slot.sheet = part.sheet;
            slot.cells.push(part.cells);
        },
        sheets: (): FortuneSheet[] =>
            slots.map(({ sheet, cells }) => ({ ...(sheet as FortuneSheet), celldata: cells.length === 1 ? cells[0] : cells.flat() })),
    };
};
